import * as React from "react";
import { RouteComponentProps, withRouter } from "react-router";
import { Card } from "@/components/ui";
import {
  Contact,
  Star,
  Palette,
  Share2,
  BookOpen,
  Scroll,
  Video,
  PlusCircle,
  AppWindow,
  LucideIcon,
  MessageSquare,
} from "lucide-react";

import { URLS } from "@/utils";
import { PlayerQuickAction } from "@/services/bedrock";

interface OwnProps {
  action: PlayerQuickAction;
  onSharePlayerCardClick?: () => void;
  onShareReviewLinkClick?: () => void;
}
type Props = OwnProps & RouteComponentProps;

const ICON_MAP: Record<string, LucideIcon> = {
  SHARE_PLAYER_CARD: Contact,
  SHARE_REVIEW_LINK: Star,
  CUSTOMIZATIONS: Palette,
  SOCIAL_NETWORKS: Share2,
  PROMPT_LIBRARY: BookOpen,
  SCRIPT_LIBRARY: Scroll,
  PROJECT_LIBRARY: Video,
  NEW_VIDEO_PROJECT: PlusCircle,
  NEW_VIDEO_PAGE: AppWindow,
  UNREPLIED_REVIEWS: MessageSquare,
};

const QuickActionsFC: React.FC<Props> = ({ action, history, onSharePlayerCardClick, onShareReviewLinkClick }) => {
  const { title, subtitle, actionStyle, actionTarget } = action;

  const cardPressed = (): void => {
    if (actionTarget === "SHARE_PLAYER_CARD") {
      onSharePlayerCardClick?.();
      return;
    }
    if (actionTarget === "SHARE_REVIEW_LINK") {
      onShareReviewLinkClick?.();
      return;
    }

    const routesMapper: { [key: string]: any } = {
      CUSTOMIZATIONS: { name: URLS.player.settingsCustomizations },
      SOCIAL_NETWORKS: { name: URLS.player.settingsSocialNetworks },
      PROMPT_LIBRARY: { name: URLS.player.prompts },
      SCRIPT_LIBRARY: { name: URLS.player.videoScripts },
      PROJECT_LIBRARY: { name: URLS.player.videoProjects },
      NEW_VIDEO_PROJECT: { name: URLS.player.videoProjects, locationValues: { showNew: true } },
      NEW_VIDEO_PAGE: { name: URLS.player.videoPages, locationValues: { showNew: true } },
      UNREPLIED_REVIEWS: { name: URLS.player.reputation.inbox },
    };

    const route = routesMapper[actionTarget];
    if (route) {
      if (actionTarget === "UNREPLIED_REVIEWS") {
        history.push({
          pathname: route.name,
          search: "?needingReply=true",
        });
      } else {
        history.push(route.name, route.locationValues);
      }
    }
  };

  const actionStyleLC = actionStyle.toLocaleLowerCase();
  const Icon = ICON_MAP[actionTarget];

  const styleStyles: Record<string, { iconColor: string; bgClass: string; borderClass: string; leftBorder: string }> = {
    urgent: {
      iconColor: "text-red-600",
      bgClass: "bg-red-50/60",
      borderClass: "border-red-100/80",
      leftBorder: "border-l-4 border-l-red-500",
    },
    premium: {
      iconColor: "text-purple-600",
      bgClass: "bg-purple-50/60",
      borderClass: "border-purple-100/80",
      leftBorder: "border-l-4 border-l-purple-500",
    },
    default: {
      iconColor: "text-slate-500",
      bgClass: "bg-slate-50/80",
      borderClass: "border-slate-200/60",
      leftBorder: "border-l-4 border-l-slate-300",
    },
  };

  const currentStyle = styleStyles[actionStyleLC] || styleStyles.default;

  return (
    <Card
      onClick={cardPressed}
      className={`w-full h-[120px] rounded-xl quickActionCard cursor-pointer transition-all duration-300 hover:shadow-md hover:scale-[1.015] active:scale-[0.99] flex flex-row items-center p-4 gap-4 ${currentStyle.leftBorder}`}
    >
      {/* Text Column */}
      <div className="flex flex-col gap-1 min-w-0 flex-1 text-left select-none">
        <div className="text-base font-bold text-slate-800 leading-tight tracking-tight">{title}</div>
        <p className="text-md text-slate-500 font-medium leading-normal line-clamp-3">{subtitle}</p>
      </div>

      {/* Beautiful Icon Box on the Right */}
      {Icon && (
        <div
          className={`size-14 rounded-2xl flex items-center justify-center border ${currentStyle.bgClass} ${currentStyle.borderClass} shrink-0 shadow-sm animate-in zoom-in-95 duration-200 select-none`}
        >
          <Icon className={`size-7 ${currentStyle.iconColor}`} />
        </div>
      )}
    </Card>
  );
};

export const QuickActionCard = withRouter(QuickActionsFC);
